/**
 * Demo Mode — flag + read-only guard.
 *
 * Demo Mode is a global-admin-only presenter tool: it anonymizes every live API
 * response on the fly (see utils/demoAnonymizer.js) and makes the portal fully
 * read-only, so the real deployed portal can be screen-shared with prospective
 * clients without exposing customer-identifying data.
 *
 * This module is deliberately DEPENDENCY-FREE so both api/client.js and
 * hooks/useAuth.js can import it without creating an import cycle.
 *
 * The flag lives in localStorage and is read ONCE at module scope by its
 * consumers — toggling it always triggers a full page reload, so a cached
 * boolean can never drift from the stored value.
 */

export const DEMO_MODE_KEY = 'demoMode';

/** True when Demo Mode is switched on for this browser. */
export function isDemoMode() {
  try {
    return localStorage.getItem(DEMO_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Turn Demo Mode on/off. The caller is responsible for reloading the page —
 * module-scope `const DEMO = isDemoMode()` reads make a reload mandatory.
 */
export function setDemoMode(on) {
  try {
    if (on) localStorage.setItem(DEMO_MODE_KEY, '1');
    else localStorage.removeItem(DEMO_MODE_KEY);
  } catch { /* private-mode / storage disabled — flag simply won't persist */ }
}

/** Thrown by request() for any mutating call while Demo Mode is active. */
export class DemoReadOnlyError extends Error {
  constructor(message = 'Portal is read-only in Demo Mode.') {
    super(message);
    this.name = 'DemoReadOnlyError';
    this.isDemoReadOnly = true;
  }
}

/** Shared copy for controls we disable outright instead of letting them throw. */
export const DEMO_UNAVAILABLE_TITLE = 'Unavailable in Demo Mode';
